import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { AllCommunityModule, CellSelectionModule, ClipboardModule, ColumnMenuModule, ContextMenuModule, ModuleRegistry, RichSelectModule, RowGroupingModule, RowSelectionModule, SetFilterModule, SparklinesModule, StatusBarModule, TooltipModule, ValidationModule, } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { cn, isEqual } from '@fortem-monorepo/js-utilities';
import { Spinner } from '@fortem-monorepo/react-ui';
import DEFAULT_OPTIONS from './default-options';
import { EditEventsPlugin } from './plugins/EditEventsPlugin';
import { NoRowsOverlayPlugin } from './plugins/NoRowsOverlayPlugin';
import DEFAULT_THEME from './themes/default-theme';
const DEFAULT_PLUGINS = [EditEventsPlugin(), NoRowsOverlayPlugin()];
ModuleRegistry.registerModules([
    // Register all Community features
    AllCommunityModule,
    // Register all Enterprise features
    ValidationModule,
    ClipboardModule,
    StatusBarModule,
    RowGroupingModule,
    RowSelectionModule,
    CellSelectionModule,
    ContextMenuModule,
    RichSelectModule,
    SparklinesModule.with(AgChartsEnterpriseModule),
    SetFilterModule,
    ColumnMenuModule,
    TooltipModule,
]);
export const CustomAgGrid = forwardRef(({ size = 'default', query, rowData, updating: _updating, onEditingChange, onCellValueChanged, plugins = [], ...props }, forwardedRef) => {
    // ... The reference to the AG Grid
    const gridRef = useRef(null);
    // ... Updating state
    const [updating, setUpdating] = useState(_updating);
    useEffect(() => {
        setUpdating(_updating);
    }, [_updating]);
    // ... Required to be able to use the forwardedRef object
    useImperativeHandle(forwardedRef, () => gridRef.current, []);
    // ... The reference for all popups within the Grid
    // ... This prevents clipped popup menus due to container overflow.
    const popupParentRef = useMemo(() => {
        return document.querySelector('body');
    }, []);
    // ... Hook into the given onCellValueChanged prop
    // ... But abstract away state management
    const _onCellValueChanged = useCallback(async (params) => {
        const field = params.colDef.field;
        const oldValue = params.oldValue;
        const newValue = params.newValue;
        if (!field || newValue === undefined)
            return;
        // ignore programmatic updates (rollback)
        // edit == manual edit by a user
        if (params.source !== 'edit')
            return;
        // skip no-op edits
        if (oldValue === newValue) {
            return;
        }
        if (Array.isArray(oldValue) &&
            Array.isArray(newValue) &&
            isEqual(oldValue, newValue, false)) {
            return;
        }
        try {
            setUpdating(true);
            await onCellValueChanged?.(params);
        }
        catch (err) {
            // ... Roll-back on error
            params.node.setDataValue(field, oldValue);
            console.error('Cell update failed, rolled back:', err);
        }
        finally {
            setUpdating(false);
        }
    }, [onCellValueChanged]);
    const _onCellKeyDown = async (event) => {
        const key = event.event.key;
        const field = event.colDef.field;
        const isDelete = ['Backspace', 'Delete'].includes(key);
        const isEditable = event.colDef.editable;
        const isGroup = event.node.group;
        const isEditing = event.api.getEditingCells().length > 0;
        if (!isEditable || isGroup || !isDelete || !field || isEditing)
            return;
        // Prevent AG Grid's default "enter edit mode and stay there"
        event.event?.preventDefault();
        event.event?.stopPropagation();
        // Clear the value directly
        event.node.setDataValue(event.column, null, 'edit');
        // Optional: ensure any editor is closed
        event.api.stopEditing();
    };
    const context = {
        query,
        onEditingChange,
    };
    const _plugins = [...DEFAULT_PLUGINS, ...plugins];
    const mergedProps = useMemo(() => {
        let _props = {
            // Static options
            ...DEFAULT_OPTIONS,
            // Dynamic theme
            theme: DEFAULT_THEME,
            // Adjust popup container for visible popup elements
            popupParent: popupParentRef,
            // CellValueChanged
            onCellValueChanged: _onCellValueChanged,
            // CellKeyDown
            onCellKeyDown: _onCellKeyDown,
            // Safe row data, immutability
            rowData: rowData ? rowData : [],
            // All other props, overrides included
            ...props,
        };
        /**
         * Augment Grid Options / Props
         */
        for (const plugin of _plugins) {
            if (plugin.augmentOptions) {
                _props = plugin.augmentOptions(_props, context);
            }
        }
        return _props;
    }, [_plugins, context, rowData]);
    useEffect(() => {
        const grid = gridRef.current;
        /**
         * OnGridReady
         */
        if (grid) {
            _plugins.forEach((p) => p.onGridReady?.(grid, context));
        }
        return () => {
            /**
             * OnGridDestroy
             */
            if (grid) {
                _plugins.forEach((p) => p.onGridDestroy?.(grid, context));
            }
        };
    }, []);
    const gridWrapperRef = useRef(null);
    useEffect(() => {
        function handleDocumentClick(event) {
            const gridEl = gridWrapperRef.current;
            const api = gridRef.current?.api;
            if (!gridEl || !api)
                return;
            const isEditing = api.getEditingCells().length > 0;
            if (!gridEl.contains(event.target) && !isEditing) {
                api.clearFocusedCell();
                api.clearCellSelection?.();
                api.deselectAll();
            }
        }
        document.addEventListener('mousedown', handleDocumentClick);
        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, []);
    return (_jsxs("div", { ref: gridWrapperRef, className: cn('custom-ag-grid--container relative h-full'), children: [updating && (_jsx("div", { className: 'bg-gray-50/80 animate-pulse absolute top-0 left-0 w-full h-full flex-1 z-10', children: _jsxs("div", { className: "flex h-full flex-col justify-center items-center gap-2", children: [_jsx(Spinner, { className: 'size-10' }), _jsx("span", { className: 'tracking-wide text-gray-600 font-medium text-sm', children: "UPDATING" })] }) })), _jsx(AgGridReact, { ref: gridRef, ...mergedProps })] }));
});
